import React, { useEffect } from 'react';
import './App.css';
import './styles/colors.css';
import './styles/shared.css';
import './styles/fontSizes.css';
import './styles/imageAspectRatio.css';
import './styles/ipad.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SpaceProvider } from './contexts/SpaceContext';
import { ModeProvider } from './contexts/ModeContext';
import { AdminModeProvider } from './contexts/AdminModeContext';
import { SystemStatusProvider } from './contexts/SystemStatusContext';
import { ThemeProvider } from './contexts/ThemeContext';
import AppContent from './components/AppContent';
import ResetPasswordPage from './components/ResetPasswordPage';
import { setupIOSViewport } from './utils/iosViewport';
import { hardReload } from './utils/hardReload';
import HomeLanding from './components/HomeLanding';
import GuestCollectionView from './pages/GuestCollectionView';
import MarketplacePage from './pages/MarketplacePage';
import AuctionDetailPage from './pages/AuctionDetailPage';
import MyAuctionsPage from './pages/MyAuctionsPage';
import MyBidsPage from './pages/MyBidsPage';
import InboxPage from './pages/InboxPage';

import SharedItemView from './pages/SharedItemView';
import SubscriptionSuccessPage from './components/SubscriptionSuccessPage';
import SubscriptionCancelPage from './components/SubscriptionCancelPage';
import HelpPage from './components/HelpPage';
import CollectionTransferPage from './pages/CollectionTransferPage';
import { useFontSize } from './hooks/useFontSize';
import { useNetworkRecovery } from './hooks/useNetworkRecovery';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import NetworkStatusBanner from './components/NetworkStatusBanner';
import ErrorBoundary from './components/ErrorBoundary';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';

export interface DetectedItem {
  id?: number;
  uuid?: string;
  name: string;
  description: string;
  category: string;
  color: string;
  area?: string;
  location?: string;
  sub_location?: string;
  quantity?: number;
  sale_price?: number;
  stock_keeping?: boolean;
  quantity_min?: number;
  purchase_price?: number;
  purchased_at?: string;
  created_at?: string;
  updated_at?: string;
  is_grocery?: boolean;
  uit_id?: number | null;
  unit?: string;
  is_checked?: boolean;
  checked_at?: string | null;
}

export interface AnalysisResult {
  items: DetectedItem[];
  summary: string;
  total_items: number;
  error?: string;
}

// Helper route component: when Stripe sends users back to /profile/subscription,
// select the Profile > Subscription tab and redirect to the main app shell.
const ProfileSubscriptionRedirect: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem('app-activeTab', 'profile');
    localStorage.setItem('profile-activeTab', 'subscription');
    navigate('/', { replace: true });
  }, [navigate]);

  return null;
};

function App() {
  // Initialize font size system
  useFontSize();
  
  // Initialize network recovery for iOS WebView issues after long background
  useNetworkRecovery();
  
  // Monitor network status for poor connection handling
  const { isOnline, isSlowConnection } = useNetworkStatus();

  useEffect(() => {
    // Setup iOS Safari viewport handling
    setupIOSViewport();
    
    // Add platform class to body for platform-specific styling
    if (Capacitor.isNativePlatform()) {
      document.body.classList.add('platform-native');
      
      // Initialize Capgo Social Login for native platforms
      SocialLogin.initialize({
        google: {
          webClientId: '418534208962-gv91f90igs0mq2ioko9b01gf63dvjqua.apps.googleusercontent.com',
          iOSClientId: '418534208962-fcfeae47pcddrofkctm3bbkmqf07v2nk.apps.googleusercontent.com',
          iOSServerClientId: '418534208962-gv91f90igs0mq2ioko9b01gf63dvjqua.apps.googleusercontent.com',
          mode: 'online',
        }
      }).catch(error => {
        console.error('Failed to initialize Social Login:', error);
      });
    } else {
      document.body.classList.add('platform-web');
    }
    
    // Handle chunk loading errors during frontend deployment
    // When frontend is being rebuilt, chunk files may be temporarily unavailable
    const handleChunkError = (event: ErrorEvent | PromiseRejectionEvent) => {
      const error = 'reason' in event ? event.reason : event.error;
      const message = error?.message || error?.toString() || '';
      
      // Detect chunk loading failures (common during frontend deployment)
      const isChunkError = 
        message.includes('Loading chunk') ||
        message.includes('ChunkLoadError') ||
        message.includes('Failed to fetch dynamically imported module') ||
        (message.includes('404') && message.includes('.js'));
      
      if (isChunkError) {
        console.warn('[App] Chunk loading error detected (likely frontend deployment), hard-reloading...', error);
        // Small delay to avoid reload loop if there's a persistent issue
        setTimeout(() => {
          hardReload();
        }, 1000);
      }
    };
    
    window.addEventListener('error', handleChunkError);
    window.addEventListener('unhandledrejection', handleChunkError);
    
    // PWA prompt now handled by PWAInstallPrompt component
    
    return () => {
      window.removeEventListener('error', handleChunkError);
      window.removeEventListener('unhandledrejection', handleChunkError);
    };
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
      <Router>
        <SystemStatusProvider>
          <AuthProvider>
            <SpaceProvider>
            <ModeProvider>
            <AdminModeProvider>
              <NetworkStatusBanner isOnline={isOnline} isSlowConnection={isSlowConnection} />
              <div className={`app-container ${(!isOnline || isSlowConnection) ? 'has-network-banner' : ''}`}>
                <Routes>
                  <Route path="/" element={<AppContent />} />
                  <Route path="/profile/subscription" element={<ProfileSubscriptionRedirect />} />
                  <Route path="/signup" element={<Navigate to="/" replace />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/everybit" element={<HomeLanding />} />
                  <Route path="/inbox" element={<InboxPage />} />
                  <Route path="/marketplace" element={<MarketplacePage />} />
                  <Route path="/marketplace/auction/:auctionItemId" element={<AuctionDetailPage />} />
                  <Route path="/my-auctions" element={<MyAuctionsPage />} />
                  <Route path="/my-bids" element={<MyBidsPage />} />
                  <Route path="/marketplace/:collectionId" element={<GuestCollectionView />} />
                  <Route path="/shared/:token" element={<GuestCollectionView />} />
                  <Route path="/s/:token" element={<SharedItemView />} />
                  <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
                  <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />
                  <Route path="/help" element={<HelpPage isPublic={true} isActive={true} />} />
                  <Route path="/help/:section" element={<HelpPage isPublic={true} isActive={true} />} />
                  <Route path="/transfer/:token" element={<CollectionTransferPage />} />
                </Routes>
              </div>
            </AdminModeProvider>
            </ModeProvider>
            </SpaceProvider>
          </AuthProvider>
        </SystemStatusProvider>
      </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
